import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  SolarGenerationData,
  SolarConfig,
  SolarDataPoint
} from '../features/solar/types/solar.types';
import { DEFAULT_SOLAR_CONFIG } from '../features/solar/types/solar.types';

// Types
export interface ConsumptionDataPoint {
  timestamp: string;
  energy_kwh: number;
}

export interface PriceDataPoint {
  timestamp: string;
  price_usd_per_mwh: number;
}

export interface ConsumptionData {
  totalRecords: number;
  avgConsumption: number;
  peakDemand: number | null;
  loadFactor: number | null;
  startDate: string;
  endDate: string;
  filesProcessed?: number;
  formatUsed?: string;
  data: Array<{
    timestamp: string;
    consumption: number;
    power: number;
  }>;
  data15min?: Array<{
    fecha: string;
    value: number;
    consumo_kw: number;
    consumo_kwh: number;
  }>;
  demandAnalysis?: {
    peak_demand_kw: number;
    peak_demand_time: string;
    avg_demand_kw: number;
    min_demand_kw: number;
    load_factor: number;
    capacity_factor?: number;
  };
  statistics?: {
    total_consumption_kwh: number;
    avg_daily_consumption_kwh: number;
    peak_day: string;
    peak_day_consumption_kwh: number;
  };
  dataSample?: {
    first_rows: Array<{ date: string; time: string; value: number }>;
    last_rows: Array<{ date: string; time: string; value: number }>;
  };
}

export interface BillData {
  // Basic Info
  period: string;
  consumptionKwh: number;
  
  // Energy Charges
  energyRate: number;
  energyCharge: number;
  
  // Demand Charges HP
  demandHpKw: number;
  demandHpRate: number;
  demandHpCharge: number;
  
  // Demand Charges HFP
  demandHfpKw: number;
  demandHfpRate: number;
  demandHfpCharge: number;
  
  // Other Charges
  transmission?: number;
  lgse?: number;
  publicService?: number;
  publicServiceFet?: number;
  pdxEnergy?: number;
  pdxDemand?: number;
  complementary?: number;
  technicalMin?: number;
  powerPurchase?: number;
  otherCharges?: number;
  
  // Totals
  subtotal: number;
  iva: number;
  total: number;
}

export interface PriceData {
  records: number;
  average: number;
  peak: number;
  valley: number;
  volatilityRatio: number;
  stdDev: number;
  data: Array<{
    timestamp: string;
    priceUsd: number;
    priceClp?: number;
  }>;
}

export interface OptimizationConfig {
  // Financial Parameters
  capexPerKwh: number;
  opexPercent: number;
  discountRate: number;
  projectLifetime: number;
  electricityEscalation: number;
  currency: 'CLP' | 'USD';
  
  // Technical Parameters
  roundTripEfficiency: number;
  depthOfDischarge: number;
  degradationRate: number;
  maxDailyCycles: number;
  cRate: number;
  
  // BESS Size Range
  minPowerKw: number;
  maxPowerKw: number;
  powerStepKw: number;
  minEnergyKwh: number;
  maxEnergyKwh: number;
  energyStepKwh: number;
  
  // Selected BESS Size
  bessCapacityKwh?: number;
  bessPowerKw?: number;
  
  // Grid Connection
  maxGridConnectionKw?: number;
  
  // Generator Settings (for comparison)
  useGenerator?: boolean;
  generatorCostPerHour?: number;
  generatorHoursPerDay?: number;
  
  // Optimization Settings
  optimizationPeriod: 'month' | 'year' | '3years';
  includeSensitivity: boolean;
  priceVolatilityRange: number;
}

export interface OptimizationResult {
  annualSavings: number;
  roi: number;
  paybackPeriod: number;
  npv: number;
  irr: number;
  optimalBessPower: number;
  optimalBessEnergy: number;
  
  // Additional details
  energyArbitrageSavings?: number;
  demandChargeSavings?: number;
  totalSavings?: number;
  generatorSavings?: number;
  configurations?: Array<any>;  // Full configuration results
  optimal?: any;
  baseParameters?: any;
  sensitivityAnalysis?: {
    priceVolatility: Array<{ variation: number; npv: number }>;
    efficiency: Array<{ value: number; npv: number }>;
    degradation: Array<{ rate: number; npv: number }>;
  };
  
  // Pattern analysis
  patternInfo?: any;
  detailedPatterns?: {
    consumption: {
      peak_hours: string[];
      off_peak_hours: string[];
      avg_peak_demand: number;
      avg_off_peak_demand: number;
      hourly_profile: number[];
    };
    prices: {
      high_price_hours: string[];
      low_price_hours: string[];
      price_spread: number;
      volatility: number;
      hourly_profile: number[];
    };
    strategy: {
      charging_hours: string[];
      discharging_hours: string[];
      arbitrage_opportunities: number;
      utilization_estimate: number;
    };
  };

  // Solar integration
  solar_enabled?: boolean;
  solar_config?: {
    capacity_kwp: number;
    inverter_kw?: number;
    capex_per_kwp?: number;
    opex_per_kwp_year?: number;
  } | null;
  combined_financials?: {
    total_annual_savings: number;
    total_capex: number;
    npv: number;
    irr: number;
    payback_years: number;
    roi: number;
    bess_savings: {
      annual_total: number;
      energy_arbitrage: number;
      peak_shaving: number;
      generator_replacement: number;
    };
    bess_capex: number;
    solar_savings: {
      annual_total: number;
      direct_self_consumption: number;
      via_bess: number;
      curtailment_loss: number;
    };
    solar_capex: number;
    solar_annual_opex: number;
    energy_summary: {
      solar_generation_kwh: number;
      solar_self_consumption_kwh: number;
      solar_to_bess_kwh: number;
      solar_curtailed_kwh: number;
      self_consumption_ratio: number;
    };
  } | null;
}

interface ProcessingState {
  consumption: boolean;
  bill: boolean;
  prices: boolean;
  solar: boolean;
  optimization: boolean;
}

interface BESSStore {
  // Data States
  consumption: ConsumptionData | null;
  bill: BillData | null;
  prices: PriceData | null;
  // configuration removed - now handled by useConfiguration hook
  optimization: OptimizationResult | null;

  // Solar States
  solarGeneration: SolarGenerationData | null;
  solarConfig: SolarConfig;
  alignedSolar: SolarDataPoint[] | null;

  // Aligned Data States
  alignedConsumption: ConsumptionDataPoint[] | null;
  alignedPrices: PriceDataPoint[] | null;
  
  // UI States
  currentStep: 1 | 2 | 3 | 4;
  isProcessing: ProcessingState;
  errors: {
    consumption?: string;
    bill?: string;
    prices?: string;
    solar?: string;
    optimization?: string;
  };

  // Actions - Data
  setConsumption: (data: ConsumptionData | null) => void;
  setBill: (data: BillData | null) => void;
  setPrices: (data: PriceData | null) => void;
  // setConfiguration removed - now handled by useConfiguration hook
  setOptimization: (data: OptimizationResult | null) => void;
  setAlignedData: (consumption: ConsumptionDataPoint[] | null, prices: PriceDataPoint[] | null) => void;

  // Actions - Solar
  setSolarGeneration: (data: SolarGenerationData | null) => void;
  setSolarConfig: (config: Partial<SolarConfig>) => void;
  setSolarEnabled: (enabled: boolean) => void;
  setAlignedSolar: (data: SolarDataPoint[] | null) => void;
  resetSolar: () => void;
  
  // Actions - Processing
  setProcessing: (key: keyof ProcessingState, value: boolean) => void;
  setError: (key: keyof ProcessingState, error: string | undefined) => void;
  
  // Actions - Navigation
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: 1 | 2 | 3 | 4) => void;
  
  // Actions - Reset
  resetAll: () => void;
  resetData: () => void;
  
  // Computed
  canProceedToStep2: () => boolean;
  canProceedToStep3: () => boolean;
  canProceedToStep4: () => boolean;
  hasAllRequiredData: () => boolean;
}

const initialState = {
  consumption: null,
  bill: null,
  prices: null,
  // configuration removed - now handled by useConfiguration hook
  optimization: null,
  // Solar
  solarGeneration: null,
  solarConfig: DEFAULT_SOLAR_CONFIG,
  alignedSolar: null,
  // Aligned data
  alignedConsumption: null,
  alignedPrices: null,
  currentStep: 1 as const,
  isProcessing: {
    consumption: false,
    bill: false,
    prices: false,
    solar: false,
    optimization: false
  },
  errors: {}
};

export const useBESSStore = create<BESSStore>()(
  devtools(
    immer((set, get) => ({
      ...initialState,
      
      // Data Actions
      setConsumption: (data) => set((state) => {
        state.consumption = data;
        state.errors.consumption = undefined;
      }),
      
      setBill: (data) => set((state) => {
        state.bill = data;
        state.errors.bill = undefined;
      }),
      
      setPrices: (data) => set((state) => {
        state.prices = data;
        state.errors.prices = undefined;
      }),

      // setConfiguration removed - now handled by useConfiguration hook

      setOptimization: (data) => set((state) => {
        state.optimization = data;
        state.errors.optimization = undefined;
      }),
      
      setAlignedData: (consumption, prices) => set((state) => {
        state.alignedConsumption = consumption;
        state.alignedPrices = prices;
      }),

      // Solar Actions
      setSolarGeneration: (data) => set((state) => {
        state.solarGeneration = data;
        state.errors.solar = undefined;

        // Auto-populate solar config with capacity values from parsed data
        if (data) {
          // Use peak generation as a proxy for system capacity (kWp)
          // This is a reasonable approximation since peak generation usually equals installed capacity
          if (data.peakGeneration > 0 && state.solarConfig.capacityKwp === 0) {
            state.solarConfig.capacityKwp = Math.round(data.peakGeneration);
            // Inverter size is typically equal to or slightly larger than DC capacity
            state.solarConfig.inverterSizeKw = Math.round(data.peakGeneration);
          }
        }
      }),

      setSolarConfig: (config) => set((state) => {
        state.solarConfig = { ...state.solarConfig, ...config };
      }),

      setSolarEnabled: (enabled) => set((state) => {
        state.solarConfig.solarEnabled = enabled;
        // Don't clear solarGeneration/alignedSolar here: the toggle is a preference,
        // not a destructive action. Backend data persists; use /api/solar/clear to
        // actually delete. Clearing here caused DataInputPage's summary-loading
        // effect to refetch and force-re-enable, making "No, solo BESS" unclickable.
        state.errors.solar = undefined;
      }),

      setAlignedSolar: (data) => set((state) => {
        state.alignedSolar = data;
      }),

      resetSolar: () => set((state) => {
        state.solarGeneration = null;
        state.solarConfig = DEFAULT_SOLAR_CONFIG;
        state.alignedSolar = null;
        state.errors.solar = undefined;
      }),

      // Processing Actions
      setProcessing: (key, value) => set((state) => {
        state.isProcessing[key] = value;
      }),
      
      setError: (key, error) => set((state) => {
        state.errors[key] = error;
        state.isProcessing[key] = false;
      }),
      
      // Navigation Actions
      nextStep: () => set((state) => {
        if (state.currentStep < 4) {
          state.currentStep++;
        }
      }),
      
      previousStep: () => set((state) => {
        if (state.currentStep > 1) {
          state.currentStep--;
        }
      }),
      
      goToStep: (step) => set((state) => {
        state.currentStep = step;
      }),
      
      // Reset Actions
      resetAll: () => set(() => initialState),
      
      resetData: () => set((state) => {
        state.consumption = null;
        state.bill = null;
        state.prices = null;
        state.solarGeneration = null;
        state.alignedSolar = null;
        state.optimization = null;
        state.errors = {};
      }),
      
      // Computed
      canProceedToStep2: () => {
        const state = get();
        const baseDataReady = !!(state.consumption && state.bill && state.prices);
        // If solar is enabled, also require solar data
        if (state.solarConfig.solarEnabled) {
          return baseDataReady && !!state.solarGeneration;
        }
        return baseDataReady;
      },
      
      canProceedToStep3: () => {
        const state = get();
        return !!(state.alignedConsumption && state.alignedPrices);
      },

      canProceedToStep4: () => {
        const state = get();
        return !!state.optimization;
      },
      
      hasAllRequiredData: () => {
        const state = get();
        const baseDataReady = !!(state.consumption && state.bill && state.prices);
        // If solar is enabled, also require solar data
        if (state.solarConfig.solarEnabled) {
          return baseDataReady && !!state.solarGeneration;
        }
        return baseDataReady;
      }
    }))
  )
);